import { useCallback, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Heading, Text } from 'react-aria-components';
import { Controller, useForm } from 'react-hook-form';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import TextField from 'src/components/ui/TextField';
import Button from 'src/components/ui/Button';
import GoogleLoginButton from 'src/components/auth/GoogleLoginButton';
import OnboardingPendingPanel, {
  isOnboardingPending,
} from 'src/components/auth/OnboardingPendingPanel';
import LinkButton from 'src/components/ui/LinkButton';
import WhiteLabelsForm from 'src/components/auth/WhiteLabelsForm';
import PageLoader from 'src/components/ui/PageLoader';
import MailIcon from 'src/assets/icons/envelope.svg';
import ArrowRightIcon from 'src/assets/icons/arrow-right-1.svg';
import ArrowLeftIcon from 'src/assets/icons/direction-left.svg';
import MAGIC_LOGIN_MUTATION from 'src/graphql/mutation/magic-login.graphql';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ENV_MODE, isWhiteLabel } from 'src/utils';
import { AccessCodesSchema } from 'src/hooks/useAccessCodesEffect';
import { graphQLErrorNotification } from 'src/utils/notifications';
import useWhitelabel from 'src/hooks/useWhitelabel';
import WEBAUTHN_CHECK from 'src/graphql/query/webauth-available.graphql';
import usePasskeyLogin from 'src/hooks/usePasskeyLogin';

import type { ApolloError } from '@apollo/client';

const SignInPage = () => {
  const { samlEnabled, title, loading } = useWhitelabel();

  useEffect(() => {
    document.body.classList.add('login');

    return () => {
      document.body.classList.remove('login');
    };
  }, []);

  if (loading) return <PageLoader />;

  if (samlEnabled) return <WhitelabelLogin title={title} />;

  return <EmailLogin />;
};

const SignInSchema = z.object({
  email: z
    .string()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address, like name@example.com.'),
});

type SignInSchemaType = z.infer<typeof SignInSchema>;

const SignInForm = ({
  accessCodes,
  onError,
  ...scope
}: LoginSchemaType & {
  onError: (error: ApolloError, email: string) => void;
}) => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const { handleSubmit, clearErrors, reset, control } =
    useForm<SignInSchemaType>({
      resolver: zodResolver(SignInSchema),
      defaultValues: {
        email: '',
      },
    });

  const { passkeyLogin, loading: passkeyLoading } = usePasskeyLogin();
  const [checkAuthn, { loading: checkLoading }] = useLazyQuery(WEBAUTHN_CHECK);

  const [magicLogin, { loading }] = useMutation(MAGIC_LOGIN_MUTATION, {
    // The page decides between a panel and a toast, so the global error
    // toast in errorLink is switched off for this mutation.
    context: { notifyOnError: false },
    onCompleted: async (data, clientOptions) => {
      if (data.magicLogin) {
        const email = clientOptions?.variables?.input?.email;

        await navigate({
          to: '/magic-link',
          replace: true,
          search: { email },
        });
      }
    },
    onError: (error, clientOptions) =>
      onError(error, clientOptions?.variables?.input?.email),
  });

  const onMagicLogin = useCallback(
    async ({ email = '' }) => {
      const recaptcha = executeRecaptcha
        ? await executeRecaptcha('login')
        : null;
      const input = { email, recaptcha: recaptcha || null };

      await magicLogin({
        variables: {
          input,
          scope,
          accessCodes,
          whitelabelKey: isWhiteLabel ? ENV_MODE : null,
        },
      });
    },
    [accessCodes, magicLogin, executeRecaptcha, scope]
  );

  const onSubmit = useCallback(
    async ({ email = '' }) => {
      clearErrors();

      const authnCheck = await checkAuthn({ variables: { email } });

      if (authnCheck?.data.available) {
        await passkeyLogin({ email, onCancel: () => onMagicLogin({ email }) });
      } else {
        await onMagicLogin({ email });
      }

      reset();
    },
    [clearErrors, checkAuthn, reset, passkeyLogin, onMagicLogin]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState: { invalid, error } }) => (
          <TextField
            {...field}
            Icon={MailIcon}
            label="Email"
            placeholder="name@example.com"
            description="No account yet? We'll create a student account for you."
            validationBehavior="aria"
            isInvalid={invalid}
            errorMessage={error?.message}
            isRequired
            autoComplete="email"
          />
        )}
      />
      <Button
        className="w-full justify-center mt-8 gap-2"
        type="submit"
        isDisabled={loading || checkLoading || passkeyLoading}
        loading={loading || checkLoading || passkeyLoading}
      >
        Continue
        <ArrowRightIcon className="w-3 h-3 text-primary-45" />
      </Button>
    </form>
  );
};

const EmailLogin = ({ onBack }: { onBack?: () => void }) => {
  const { institutionId, programId, campusId, classId, accessCodes } =
    Route.useSearch();

  const whiteLabelsEnabled = false;

  const [whitelabelMode, setWhitelabelMode] = useState(false);
  // Set while the under-review panel is showing. Google sign-in gives the
  // client no email, so the panel is then shown without one.
  const [pending, setPending] = useState<{ email?: string } | null>(null);

  const onLoginError = (error: ApolloError, email?: string) => {
    if (isOnboardingPending(error)) {
      setPending({ email });
      return;
    }

    graphQLErrorNotification(error);
  };

  if (pending) {
    return (
      <OnboardingPendingPanel
        email={pending.email}
        onBack={() => setPending(null)}
      />
    );
  }

  if (whitelabelMode) {
    return <WhiteLabelsForm onBack={() => setWhitelabelMode(false)} />;
  }

  return (
    <div>
      <Heading level={2} className="text-surface-alt-90 text-4xl font-bold">
        Welcome!
      </Heading>
      <Text
        elementType="p"
        className="text-sm text-surface-alt-75 font-normal mt-2 mb-8"
      >
        Enter your email to access your course materials.
      </Text>
      <SignInForm
        institutionId={institutionId}
        classId={classId}
        campusId={campusId}
        programId={programId}
        accessCodes={accessCodes}
        onError={onLoginError}
      />

      {!isWhiteLabel && (
        <>
          <div className="min-w-0 flex-1 flex items-center gap-2.5 py-2">
            <span className="flex-1 h-px bg-surface-alt-ghost-10" />
            <Text elementType="span" className="text-sm text-surface-alt-75">
              or continue with
            </Text>
            <span className="flex-1 h-px bg-surface-alt-ghost-10" />
          </div>
          <div className="flex flex-col items-center gap-2 mt-2">
            <GoogleLoginButton
              theme="filled_black"
              shape="circle"
              useOneTap
              institutionId={institutionId}
              classId={classId}
              campusId={campusId}
              programId={programId}
              onLoginError={(error) => onLoginError(error)}
            />
            {whiteLabelsEnabled && (
              <LinkButton
                onPress={() => setWhitelabelMode(true)}
                hasArrow={false}
                className="text-xs"
              >
                Continue with Whitelabel
              </LinkButton>
            )}
          </div>
        </>
      )}
      <CreatorStudioLine />
      {Boolean(onBack) && (
        <div className="mt-2 text-center -mb-4">
          <LinkButton onPress={onBack} hasArrow={false}>
            <ArrowLeftIcon className="w-3 h-3 text-primary-45 ml-1" />
            Go Back
          </LinkButton>
        </div>
      )}
    </div>
  );
};

const WhitelabelLogin = ({ title }: { title: string }) => {
  const [redirecting, setRedirecting] = useState(false);
  const [formActive, setFormActive] = useState(false);

  if (formActive) return <EmailLogin onBack={() => setFormActive(false)} />;

  return (
    <div>
      <Heading level={2} className="text-surface-alt-90 text-4xl font-bold">
        Sign In
      </Heading>
      <Text
        elementType="p"
        className="text-sm text-surface-alt-75 font-normal mt-2 mb-8"
      >
        Choose how you&apos;d like to sign in.
      </Text>

      <Button
        loading={redirecting}
        className="w-full justify-center mt-8 gap-1"
        onPress={() => {
          setRedirecting(true);
          window.location.href = `https://${import.meta.env.VITE_API_ORIGIN}/sso/saml/login/${ENV_MODE}`;
        }}
      >
        Sign in with <span className="capitalize">{title}</span>
        <ArrowRightIcon className="w-3 h-3 text-primary-45 ml-1" />
      </Button>
      <Button
        ghost
        variant="surface"
        className="w-full justify-center mt-2 gap-2"
        onPress={() => setFormActive(true)}
      >
        Sign in with email
      </Button>
      <CreatorStudioLine />
    </div>
  );
};

const CreatorStudioLine = () => (
  <>
    <span className="block h-px bg-surface-alt-ghost-10 mt-6 mb-5" />
    <div className="flex flex-col items-center gap-1 text-center">
      <Text elementType="p" className="text-sm text-surface-alt-75">
        Faculty, bookstore manager, or administrator?
      </Text>
      <LinkButton
        href={`/creator-login${window.location.search}`}
        className="text-sm"
      >
        Sign In to Creator Studio
      </LinkButton>
    </div>
  </>
);

const LoginSchema = z
  .object({
    institutionId: z.number().optional(),
    programId: z.number().optional(),
    campusId: z.number().optional(),
    classId: z.number().optional(),
  })
  .merge(AccessCodesSchema);

type LoginSchemaType = z.infer<typeof LoginSchema>;

export const Route = createFileRoute('/_auth/login')({
  validateSearch: (search) => LoginSchema.parse(search),
  beforeLoad: async () => {
    // BNC is not using the default login page,
    // so we need to redirect them to the creator login page
    if (ENV_MODE === 'bnc') {
      redirect({
        to: '/creator-login',
        throw: true,
      });
    }
  },
  component: SignInPage,
});
